import { Box, Skeleton, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { useSubscriptionTier } from "src/data/subscription/subscriptionTier";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";
import {
  colorAccent,
  colorActionPrimary,
  colorActionPrimaryText,
  colorTextDisabled,
  radiusCard,
  shadowCard,
} from "src/design/tokens";

interface SubscriptionPillProps {
  restaurantId: string;
}

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: colorTextDisabled,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "15px", fontWeight: 600, color: colorActionPrimaryText }}
    >
      {value}
    </Typography>
  </Box>
);

const Separator = () => (
  <Box
    sx={{
      width: "1px",
      alignSelf: "stretch",
      backgroundColor: "rgba(255,255,255,0.15)",
    }}
  />
);

/**
 * The restaurant's plan and next billing date, with a shortcut to the
 * Configuration page where the subscription is actually managed.
 */
export const SubscriptionPill = ({ restaurantId }: SubscriptionPillProps) => {
  const { t, i18n } = useTranslation();
  const { data: subscription, isLoading } = useSubscriptionTier(restaurantId);
  // The tier's display name and price live in the public catalog, matched by
  // name — the subscription record only carries the tier id.
  const { data: catalog } = useGetSubscriptionTiers();
  const plan = catalog?.find((tier) => tier.name === subscription?.tier);

  const planLabel = subscription
    ? [
        t(tierTitleKey(subscription.tier), {
          defaultValue: subscription.tier,
        }),
        plan
          ? `${formatPrice(plan.monthlyPrice, i18n.language)} ${t(
              "landingPage.subscriptionTiers.priceSuffix",
            )}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : t("dashboard.subscription.none");

  const billingLabel = subscription?.nextBillingCycle
    ? dayjs(subscription.nextBillingCycle).format("MMMM D, YYYY")
    : "—";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        backgroundColor: colorActionPrimary,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        px: 2,
        py: 1.5,
        flexWrap: "wrap",
      }}
    >
      {isLoading ? (
        <Skeleton variant="rounded" width={260} height={38} />
      ) : (
        <>
          <Field
            label={t("dashboard.subscription.plan")}
            value={planLabel}
          />
          <Separator />
          <Field
            label={t("dashboard.subscription.nextBilling")}
            value={billingLabel}
          />
          <Separator />
          <Typography
            component={Link}
            to={`/restaurant/${restaurantId}/configuration`}
            sx={{
              fontSize: "12.5px",
              fontWeight: 600,
              color: colorAccent,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("dashboard.subscription.manage")}
          </Typography>
        </>
      )}
    </Box>
  );
};
